import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Building2, ArrowRight } from "lucide-react";

const EXAMPLES = [
  "I'm considering increasing my main product price by 15%.",
  "Should I open a second retail location downtown?",
  "I want to launch a subscription plan alongside one-time purchases.",
  "I'm thinking about hiring my first full-time salesperson.",
];

export default function NewDecision() {
  const { business } = useAuth();
  const [text, setText] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    if (text.trim().length < 8) { toast.error("Describe your decision in a sentence or two."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/decisions", { decision_text: text, context });
      navigate(`/decisions/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  if (business === null) {
    return (
      <AppShell>
        <div className="max-w-lg mx-auto bg-white border border-slate-200 rounded-xl p-8 shadow-sm text-center mt-8">
          <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-4" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-2">Create your business profile first</h2>
          <p className="text-slate-500 mb-6">We match analogs against your specific business. Set it up once.</p>
          <Button onClick={() => navigate("/setup")} className="bg-slate-900 hover:bg-slate-800 rounded-lg">Set up profile</Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mb-2">Analyze a decision</h1>
        <p className="text-slate-500 mb-8">Describe the decision you're weighing. We'll find real businesses that made a similar move.</p>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <Label className="text-sm text-slate-700">Your decision</Label>
          <Textarea data-testid="decision-input" value={text} onChange={(e) => setText(e.target.value)} rows={4}
            placeholder="e.g. I am considering increasing the price of my flagship product from ₹999 to ₹1,199."
            className="mt-1.5 rounded-lg text-base p-4 focus-visible:ring-1 focus-visible:ring-slate-900" />

          <div className="flex flex-wrap gap-2 mt-3">
            {EXAMPLES.map((ex, i) => (
              <button key={i} onClick={() => setText(ex)} data-testid={`example-${i}`}
                className="text-xs text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-full px-3 py-1.5 transition-colors">
                {ex}
              </button>
            ))}
          </div>

          <div className="mt-5">
            <Label className="text-sm text-slate-700">Supporting context <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input data-testid="context-input" value={context} onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. Ingredient costs rose 20% this year; competitors haven't changed prices."
              className="mt-1.5 rounded-lg" />
          </div>

          <Button onClick={submit} disabled={loading} data-testid="find-comparables-button"
            className="w-full mt-6 bg-slate-900 hover:bg-slate-800 rounded-lg h-12 text-base gap-2">
            {loading ? "Starting…" : <>Find Comparable Decisions <ArrowRight className="w-4 h-4" /></>}
          </Button>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4 font-mono">
          Analysis is evidence-first. We label FACT vs AI INFERENCE vs UNKNOWN and never predict your future.
        </p>
      </div>
    </AppShell>
  );
}
